// SavedView — "Save for later" list.
//
// Reads the saved list from `useSaved`, renders one row per
// item, and provides:
//   - Header 56px: "Saved · N" + "Clear all" ghost button (with confirm
//     dialog).
//   - Row 64px: avatar + two-line body (sender + timestamp on line 1,
//     body preview + room chip on line 2) + Remove icon on hover.
//   - Empty state centered.
//   - Click-to-jump: delegates to `MatrixContext.jumpToEvent` which sets
//     the active room + publishes a `pendingJump` for the timeline.
//
// Row content beyond `roomId + eventId + savedAt` (sender name,
// body preview) isn't stored in account data on purpose — the source
// room is the authoritative source. Until the timeline ships resolved
// event previews (the shared message lookup lands with #1292), the row
// shows the room name as the primary descriptor, the saved timestamp,
// the room chip, and a muted "Preview when opened" hint so users know
// the body becomes visible on jump. The click-to-jump behaviour still
// works because the Matrix event reference is stored in full.

import { useMemo, useState, type CSSProperties } from "react";
import { Bookmark, Hash, X } from "lucide-react";
import { RelativeLabel } from "@nurion/ui/relative-label";

import { ConfirmDialog } from "~/components/ConfirmDialog";
import { EmptyState } from "~/components/EmptyState";
import { useMatrix, useMatrixRooms } from "~/context/MatrixContext";
import { useToast } from "~/context/ToastContext";
import { useSaved } from "~/hooks/useSaved";
import { getInitials } from "~/lib/initials";
import type { RoomSummary, SavedItem } from "~/lib/matrix";
import { absoluteFallback, LOCAL_ZONE } from "~/lib/relative-time";
import { ICON_STROKE } from "~/lib/icon";

// Hover-reveal for the Remove button on each saved row. Injected once
// on module load so the view doesn't mutate document during render.
const ROW_STYLE_ID = "chat-saved-row-styles";
function injectRowStyles(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(ROW_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = ROW_STYLE_ID;
  style.textContent = `
    .chat-saved-row__remove {
      opacity: 0;
      transition: opacity var(--chat-motion-fast, 120ms) var(--chat-motion-ease, ease);
    }
    .chat-saved-row:hover .chat-saved-row__remove,
    .chat-saved-row:focus-within .chat-saved-row__remove {
      opacity: 1;
    }
    .chat-saved-row:hover {
      background: var(--chat-surface-raised);
    }
  `;
  document.head.appendChild(style);
}
injectRowStyles();

export function SavedView() {
  const { items } = useSaved();
  const { jumpToEvent, unsaveEvent } = useMatrix();
  const rooms = useMatrixRooms();
  const { push: pushToast } = useToast();
  const [confirmingClear, setConfirmingClear] = useState(false);

  // Build a lookup so each row can resolve its room name without re-scanning rooms per render.
  const roomById = useMemo(() => {
    const map = new Map<string, RoomSummary>();
    for (const room of rooms) map.set(room.id, room);
    return map;
  }, [rooms]);

  const handleRemove = async (item: SavedItem) => {
    try {
      await unsaveEvent(item.eventId);
    } catch (err) {
      console.error("SavedView: unsaveEvent failed", err);
      pushToast({ message: "Couldn't remove. Try again.", tone: "danger" });
    }
  };

  const handleClearAll = async () => {
    setConfirmingClear(false);
    try {
      for (const item of items) {
        await unsaveEvent(item.eventId);
      }
    } catch (err) {
      console.error("SavedView: clear visible saved failed", err);
      pushToast({ message: "Couldn't clear saved. Try again.", tone: "danger" });
    }
  };

  return (
    <ViewShell
      title="Saved"
      count={items.length}
      action={
        items.length > 0
          ? {
              label: "Clear all",
              onClick: () => setConfirmingClear(true),
            }
          : null
      }
    >
      {items.length === 0 ? (
        <EmptyState
          icon={
            <Bookmark
              size={48}
              strokeWidth={ICON_STROKE}
              style={{ color: "var(--chat-fg-muted)", opacity: 0.6 }}
            />
          }
          primary="Nothing saved yet"
          secondary="Save messages to find them here."
        />
      ) : (
        <ul
          style={{
            flex: 1,
            overflowY: "auto",
            listStyle: "none",
            margin: 0,
            padding: 0,
          }}
        >
          {items.map((item) => (
            <li key={item.eventId}>
              <SavedRow
                item={item}
                room={roomById.get(item.roomId) ?? null}
                onJump={() => jumpToEvent({ roomId: item.roomId, eventId: item.eventId })}
                onRemove={() => void handleRemove(item)}
              />
            </li>
          ))}
        </ul>
      )}
      {confirmingClear && (
        <ConfirmDialog
          title="Clear all saved messages?"
          body="This removes every saved message. It can't be undone."
          confirmLabel="Clear all"
          onConfirm={() => void handleClearAll()}
          onCancel={() => setConfirmingClear(false)}
        />
      )}
    </ViewShell>
  );
}

/* ─── shell + header ─────────────────────────────────────────── */

function ViewShell({
  title,
  count,
  action,
  children,
}: {
  title: string;
  count: number;
  action?: { label: string; onClick: () => void } | null;
  children: React.ReactNode;
}) {
  return (
    <section
      data-testid="saved-view"
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minWidth: 0,
        height: "100%",
        background: "var(--chat-surface-app)",
        color: "var(--chat-fg-primary)",
        fontFamily: "var(--chat-font-sans)",
      }}
    >
      <Header title={title} count={count} action={action ?? null} />
      {children}
    </section>
  );
}

function Header({
  title,
  count,
  action,
}: {
  title: string;
  count: number;
  action: { label: string; onClick: () => void } | null;
}) {
  return (
    <header
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-3)",
        height: "var(--chat-timeline-room-header-height)",
        padding: "0 var(--chat-space-4)",
        borderBottom: "1px solid var(--chat-surface-border)",
        flexShrink: 0,
      }}
    >
      <Bookmark
        size={16}
        strokeWidth={ICON_STROKE}
        style={{ color: "var(--chat-fg-secondary)", flexShrink: 0 }}
      />
      <h2
        style={{
          flex: 1,
          margin: 0,
          color: "var(--chat-fg-primary)",
          fontFamily: "var(--chat-font-display)",
          fontSize: "var(--chat-type-display-sm-size)",
          lineHeight: "var(--chat-type-display-sm-line)",
          fontWeight: 300,
        }}
      >
        {title}
        <span
          style={{
            marginLeft: "var(--chat-space-2)",
            color: "var(--chat-fg-secondary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-md-size)",
            lineHeight: "var(--chat-type-body-md-line)",
            fontWeight: 400,
          }}
        >
          · {count}
        </span>
      </h2>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="chat-button"
          style={ghostButtonStyle}
        >
          {action.label}
        </button>
      )}
    </header>
  );
}

/* ─── row ────────────────────────────────────────────────────── */

function SavedRow({
  item,
  room,
  onJump,
  onRemove,
}: {
  item: SavedItem;
  room: RoomSummary | null;
  onJump: () => void;
  onRemove: () => void;
}) {
  const senderLabel = room?.name ?? "Unknown room";
  return (
    <div
      className="chat-saved-row"
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-3)",
        // 64px row height pinned by the brief. Inline — the Mentions
        // row shares this dimension but it isn't a first-class token
        // yet; hoisting it here would be premature abstraction.
        height: 64,
        padding: "0 var(--chat-space-4)",
        borderBottom: "1px solid var(--chat-surface-border)",
      }}
    >
      <button
        type="button"
        onClick={onJump}
        aria-label={`Jump to saved message in ${senderLabel}`}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--chat-space-3)",
          flex: 1,
          minWidth: 0,
          height: "100%",
          border: "none",
          background: "transparent",
          color: "inherit",
          font: "inherit",
          textAlign: "left",
          cursor: "pointer",
          padding: 0,
        }}
      >
        <RoomAvatar room={room} />
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: "var(--chat-space-2)",
              minWidth: 0,
            }}
          >
            <span
              style={{
                color: "var(--chat-fg-primary)",
                fontFamily: "var(--chat-font-sans)",
                fontSize: "var(--chat-type-body-md-size)",
                lineHeight: "var(--chat-type-body-md-line)",
                fontWeight: 400,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {senderLabel}
            </span>
            <span
              style={{
                color: "var(--chat-fg-secondary)",
                fontFamily: "var(--chat-font-sans)",
                fontSize: "var(--chat-type-body-sm-size)",
                lineHeight: "var(--chat-type-body-sm-line)",
                flexShrink: 0,
              }}
            >
              <RelativeLabel
                value={new Date(item.savedAt).toISOString()}
                zone={LOCAL_ZONE}
                fallback={absoluteFallback(item.savedAt)}
              />
            </span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--chat-space-2)",
              minWidth: 0,
            }}
          >
            <RoomChip room={room} />
            <span
              style={{
                color: "var(--chat-fg-muted)",
                fontFamily: "var(--chat-font-sans)",
                fontSize: "var(--chat-type-body-sm-size)",
                lineHeight: "var(--chat-type-body-sm-line)",
                fontStyle: "italic",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              Preview when opened
            </span>
          </div>
        </div>
      </button>
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove from Saved"
        title="Remove from Saved"
        className="chat-saved-row__remove chat-button"
        style={{
          // 20×20 remove icon per the ticket's visual specs. Inline —
          // the surface is unique to this row.
          width: 20,
          height: 20,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          border: "none",
          borderRadius: "var(--chat-radius-sm)",
          background: "transparent",
          color: "var(--chat-fg-muted)",
          cursor: "pointer",
          padding: 0,
          flexShrink: 0,
        }}
      >
        <X size={14} strokeWidth={ICON_STROKE} />
      </button>
    </div>
  );
}

function RoomAvatar({ room }: { room: RoomSummary | null }) {
  const label = room?.name ?? "?";
  const initials = getInitials(label);
  return (
    <span
      aria-hidden
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 32,
        height: 32,
        borderRadius: room?.isDirect ? "var(--chat-radius-full)" : "var(--chat-radius-md)",
        background: "var(--chat-surface-raised)",
        color: room ? "var(--chat-fg-secondary)" : "var(--chat-fg-muted)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: 12,
        fontWeight: 500,
        flexShrink: 0,
      }}
    >
      {initials || "–"}
    </span>
  );
}

function RoomChip({ room }: { room: RoomSummary | null }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "0 6px",
        height: 18,
        borderRadius: "var(--chat-radius-sm)",
        background: "var(--chat-surface-raised)",
        color: "var(--chat-fg-secondary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-xs-size)",
        lineHeight: "var(--chat-type-body-xs-line)",
        flexShrink: 0,
      }}
    >
      <Hash size={10} strokeWidth={ICON_STROKE} style={{ color: "var(--chat-fg-secondary)" }} />
      {room?.name ?? "unknown"}
    </span>
  );
}

/* ─── shared styles ─────────────────────────────────────────── */

const ghostButtonStyle: CSSProperties = {
  padding: "var(--chat-space-1) var(--chat-space-3)",
  borderRadius: "var(--chat-radius-sm)",
  border: "none",
  background: "transparent",
  color: "var(--chat-fg-secondary)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-sm-size)",
  lineHeight: "var(--chat-type-body-sm-line)",
  fontWeight: 500,
  cursor: "pointer",
};
